/**
 * Reading Bazak's public Better Stack status page. Shared so the marketing site and the platform
 * report the same state from the same source — the website used to embed Better Stack's own iframe
 * badge instead, which forced `frame-src https://status.bazak.ai` into its CSP and ignored dark mode.
 */

export const STATUS_PAGE_URL = "https://status.bazak.ai";

// Better Stack exposes the public status page as JSON: `aggregate_state` is the overall roll-up and
// each service is a `status_page_resource` in `included`.
const STATUS_JSON_URL = `${STATUS_PAGE_URL}/index.json`;

/** How long a fetched roll-up stays fresh. Both apps' proxy routes revalidate on this. */
export const STATUS_REVALIDATE_SECONDS = 60;

export interface StatusService {
  name: string;
  state: string;
  explanation?: string;
}

export interface StatusResponse {
  state: string;
  services: StatusService[];
}

interface BetterStackResource {
  type?: string;
  attributes?: {
    public_name?: string;
    status?: string;
    explanation?: string;
  };
}

export type StatusKey = "operational" | "degraded" | "down" | "unknown";

/** Unexpected/empty values fall back to 'unknown' (muted) rather than a false-alarm red. */
export function toStatusKey(state: string | undefined): StatusKey {
  switch (state) {
    case "operational":
      return "operational";
    case "degraded":
    case "maintenance":
      return "degraded";
    case "downtime":
      return "down";
    default:
      return "unknown";
  }
}

export const UNKNOWN_STATUS: StatusResponse = {
  state: "unknown",
  services: [],
};

export function parseStatus(json: unknown): StatusResponse {
  const payload = json as {
    data?: { attributes?: { aggregate_state?: string } };
    included?: BetterStackResource[];
  };
  const state = payload?.data?.attributes?.aggregate_state ?? "unknown";
  const services = (payload?.included ?? [])
    .filter(
      (item) =>
        item?.type === "status_page_resource" && item.attributes?.public_name,
    )
    .map((item) => ({
      name: item.attributes?.public_name ?? "",
      state: item.attributes?.status ?? "unknown",
      explanation: item.attributes?.explanation || undefined,
    }));
  return { state, services };
}

/**
 * `next.revalidate` is Next's extension to RequestInit. This package is only ever consumed by Next
 * apps (as source, via transpilePackages), but its own tsconfig doesn't load Next's augmentation.
 */
type NextFetchInit = RequestInit & { next?: { revalidate?: number } };

/**
 * Server-side only. Callers proxy this through their own origin so the browser isn't blocked by
 * CORS and concurrent clients share one cached upstream response.
 */
export async function fetchStatus(): Promise<StatusResponse> {
  const init: NextFetchInit = {
    next: { revalidate: STATUS_REVALIDATE_SECONDS },
  };
  const response = await fetch(STATUS_JSON_URL, init);
  if (!response.ok) {
    throw new Error(`Status page returned ${response.status}`);
  }
  return parseStatus(await response.json());
}
